import React from 'react';
import { Card, Cifra, FilaCifras, eur, num, fecha, useEndpoint } from '../ui';

/**
 * Alertas: lo que debería estar a cero, cada una con la lista de expedientes
 * enlazados. Una alerta sin precálculo al día lo dice: «sin calcular» no es
 * «cero».
 */
const P = '/portfolio-performance';
const enlace = f => `/view/${f.expedient_id}`;

export default function AlertasSection({ params }) {
  const { estado, data } = useEndpoint(`${P}/alerts`, params);
  const a = data;
  const nombre = id => (a?.names?.[id] ? a.names[id] : id || '—');
  const cargando = estado === 'loading';
  const tono = n => (n ? 'alerta' : 'ok');
  /**
   * Una alerta que sale del precálculo: si encontró algo, se enseña; si no
   * encontró nada pero hay expedientes sin calcular, NO es un cero verde, es
   * «no se sabe» (memoria `seccion-vacia-dice-lo-contrario`).
   */
  const delPrecalculo = (al, pieOk) => {
    if (!al) return { valor: '—', tono: 'neutral', pie: null };
    if (!al.total && al.not_computed)
      return { valor: '—', tono: 'neutral', pie: `Sin calcular: ${num(al.not_computed)} expedientes pendientes` };
    return {
      valor: num(al.total),
      tono: tono(al.total),
      pie: al.not_computed ? `Y ${num(al.not_computed)} sin calcular` : pieOk,
    };
  };
  const sinGestion = delPrecalculo(a?.without_management, 'En gestión viva');
  const pequena = delPrecalculo(a?.small_debt_no_contact, null);

  const deudor = { key: 'debtorname', label: 'Deudor', link: enlace, fmt: v => v || 'Ver expediente' };
  const cliente = { key: 'user_id', label: 'Cliente', fmt: nombre };
  const est = { key: 'estado', label: 'Estado' };

  return (
    <div className='space-y-4'>
      <FilaCifras>
        <Cifra
          etiqueta={`Sin gestión en ${a?.without_management?.days || 30} días`}
          cargando={cargando}
          {...sinGestion}
        />
        <Cifra etiqueta='Deuda < 100 € sin contacto' cargando={cargando} {...pequena} />
        <Cifra
          etiqueta='Líneas de WhatsApp calladas'
          cargando={cargando}
          valor={a ? num(a.silent_whatsapp_lines.total) : '—'}
          tono={a ? tono(a.silent_whatsapp_lines.total) : 'neutral'}
          pie={
            a ? `De ${num(a.silent_whatsapp_lines.active)} activas, más de ${a.silent_whatsapp_lines.days} días` : null
          }
        />
        <Cifra
          etiqueta='Saldados que siguen abiertos'
          cargando={cargando}
          valor={a ? num(a.paid_still_open.total) : '—'}
          tono={a ? tono(a.paid_still_open.total) : 'neutral'}
          pie={
            a?.paid_still_open?.float_residue
              ? `${num(a.paid_still_open.float_residue)} por el residuo de coma flotante`
              : null
          }
        />
        <Cifra
          etiqueta='Cobros solo en una anotación'
          cargando={cargando}
          valor={a ? num(a.payments_only_in_annotations.total) : '—'}
          tono={a ? tono(a.payments_only_in_annotations.total) : 'neutral'}
          pie='A revisar uno a uno'
        />
      </FilaCifras>

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
        <Card
          titulo='¿Qué expedientes vivos llevan un mes sin que nadie les escriba?'
          lectura='En un estado de gestión viva y sin ningún contacto saliente, por ningún canal, en 30 días. Los más olvidados, arriba.'
          nota='Sale del precálculo del embudo. Los recién importados (menos de 30 días) no cuentan.'
          estado={estado}
          pendiente={
            a && !a.without_management.total && a.without_management.not_computed
              ? `${num(a.without_management.not_computed)} expedientes vivos no tienen el precálculo al día: hasta que se recalcule, no se sabe.`
              : null
          }
          vacio={!a?.without_management?.items?.length}
          vacioTexto='Ninguno. Todos los expedientes vivos han tenido algún contacto en 30 días.'
          filas={a?.without_management?.items || []}
          csv='sin-gestion-30-dias'
          columnas={[
            deudor,
            cliente,
            est,
            { key: 'ultimo_contacto', label: 'Último contacto', fmt: v => (v ? fecha(v) : 'Nunca') },
            { key: 'saldo', label: 'Saldo', fmt: eur },
          ]}
          alto={null}
          maxFilas={50}
        />
        <Card
          titulo='¿Qué deudas pequeñas no ha contactado nadie?'
          lectura='Deuda total por debajo de 100 € y ni un solo mensaje ni llamada. Por debajo de ese umbral no se crea la tarea de primer contacto.'
          nota='El umbral es una decisión de negocio; la lista es para que sea una decisión y no un olvido.'
          estado={estado}
          pendiente={
            a && !a.small_debt_no_contact.total && a.small_debt_no_contact.not_computed
              ? `${num(a.small_debt_no_contact.not_computed)} expedientes no tienen el precálculo al día: hasta que se recalcule, no se sabe.`
              : null
          }
          vacio={!a?.small_debt_no_contact?.items?.length}
          vacioTexto='Ninguna.'
          filas={a?.small_debt_no_contact?.items || []}
          csv='deuda-pequena-sin-contacto'
          columnas={[deudor, cliente, est, { key: 'asignado', label: 'Deuda', fmt: eur }]}
          alto={null}
          maxFilas={50}
        />
        <Card
          titulo='¿Hay alguna línea de WhatsApp que haya dejado de enviar?'
          lectura='Líneas activas que llevan más de 3 días sin mandar nada, una a una: una línea muerta no se ve en el total.'
          nota='Último envío según las estadísticas diarias de cada línea. Las bloqueadas no cuentan: están paradas a propósito. Es de toda la operación.'
          estado={estado}
          vacio={!a?.silent_whatsapp_lines?.items?.length}
          vacioTexto='Todas las líneas activas han enviado en los últimos 3 días.'
          filas={a?.silent_whatsapp_lines?.items || []}
          csv='lineas-calladas'
          columnas={[
            { key: 'name', label: 'Línea' },
            { key: 'phone_id', label: 'Id' },
            { key: 'canal', label: 'Canal' },
            { key: 'last_sent_at', label: 'Último envío', fmt: v => (v ? fecha(v) : 'Nunca') },
            { key: 'days_silent', label: 'Días sin enviar', fmt: v => (v == null ? '—' : num(v)) },
          ]}
          alto={null}
        />
        <Card
          titulo='¿Qué expedientes pagados siguen abiertos?'
          lectura='Saldo a cero y un estado que no es de cierre: el sistema los sigue reclamando aunque hayan pagado.'
          nota='Saldo a cero con tolerancia de un céntimo: un pago exacto con varias facturas deja residuos como 1,42e-14 y el expediente se queda en «Plan de pagos».'
          estado={estado}
          vacio={!a?.paid_still_open?.items?.length}
          vacioTexto='Ninguno.'
          filas={a?.paid_still_open?.items || []}
          csv='saldados-abiertos'
          columnas={[
            deudor,
            cliente,
            est,
            { key: 'recuperado', label: 'Cobrado', fmt: eur },
            { key: 'residuo_flotante', label: 'Residuo', fmt: v => (v ? 'Sí' : 'No') },
          ]}
          alto={null}
          maxFilas={50}
        />
        <Card
          titulo='¿Hay cobros que solo constan en una anotación?'
          lectura='Anotaciones del periodo que dicen que ha llegado un pago y citan un importe que no está registrado. Sin registrar, no se factura.'
          nota='Da falsos positivos previsibles (cuotas futuras, totales ya registrados): se revisan uno a uno. Lee «276.00 €» como 276, no como 27.600.'
          estado={estado}
          vacio={!a?.payments_only_in_annotations?.items?.length}
          vacioTexto='Ninguno en este periodo.'
          filas={(a?.payments_only_in_annotations?.items || []).map(i => ({
            ...i,
            importes: i.importes.map(eur).join(', '),
          }))}
          csv='cobros-solo-en-anotacion'
          columnas={[
            deudor,
            cliente,
            { key: 'fecha', label: 'Anotación', fmt: fecha },
            { key: 'importes', label: 'Importe sin registrar' },
            { key: 'texto', label: 'Dice' },
          ]}
          alto={null}
          ancho
          maxFilas={50}
        />
      </div>
    </div>
  );
}
